import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { useAuth } from '../../../context/AuthContext';

const ManagerCompliance = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamCompliance = async () => {
      if (!user?.organisation_id) return;
      
      const { data, error } = await supabase
        .from('portal_users_1741860000000')
        .select(`
          first_name, 
          last_name, 
          role,
          certificates_1741860000000 (
            status,
            programmes_1741860000000 (name)
          )
        `)
        .eq('organisation_id', user.organisation_id);

      if (data) {
        const formatted = data.map(u => ({
          name: `${u.first_name} ${u.last_name}`,
          role: u.role,
          safeguarding: u.certificates_1741860000000?.some(c => c.programmes_1741860000000.name.includes('Safeguarding')),
          mca: u.certificates_1741860000000?.some(c => c.programmes_1741860000000.name.includes('MCA')),
          mh: u.certificates_1741860000000?.some(c => c.programmes_1741860000000.name.includes('Mental Health'))
        }));
        setTeam(formatted);
      }
      setLoading(false);
    };

    fetchTeamCompliance();
  }, [user]);

  const handleExportCSV = () => {
    const headers = ["Staff Member", "Role", "Safeguarding (L3)", "MCA & DoLS", "Mental Health Excellence"];
    const rows = team.map(member => [
      member.name,
      member.role,
      member.safeguarding ? 'Compliant' : 'Not Completed',
      member.mca ? 'Compliant' : 'Not Completed',
      member.mh ? 'Compliant' : 'Not Completed'
    ]);

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kajidori_Compliance_Matrix_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const StatusIcon = ({ status }) => (
    status ? (
      <div className="mx-auto w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
        <SafeIcon name="Check" className="w-4 h-4 text-green-600" />
      </div>
    ) : (
      <div className="mx-auto w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
        <SafeIcon name="X" className="w-4 h-4 text-red-600" />
      </div>
    )
  );

  return (
    <div className="max-w-6xl mx-auto">
      <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold text-navy-900">Compliance Matrix</h1>
          <p className="text-slate-500 mt-2">Strategic overview of organizational training health.</p>
        </div>
        <button 
          onClick={handleExportCSV}
          className="bg-navy-900 text-white px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 hover:bg-navy-800 transition-colors shadow-lg"
        >
          <SafeIcon name="Download" className="w-4 h-4" /> Export CSV Matrix
        </button>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="flex gap-4">
            <span className="flex items-center gap-1 text-xs text-slate-600 font-medium">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div> Compliant
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-600 font-medium">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div> Action Needed
            </span>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-white border-b border-slate-200">
              <tr>
                <th className="p-4 font-semibold text-slate-600">Staff Member</th>
                <th className="p-4 font-semibold text-slate-600 text-center border-l border-slate-100">Safeguarding (L3)</th>
                <th className="p-4 font-semibold text-slate-600 text-center border-l border-slate-100">MCA & DoLS</th>
                <th className="p-4 font-semibold text-slate-600 text-center border-l border-slate-100">Mental Health Exc.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="4" className="p-12 text-center text-slate-400">Loading matrix data...</td></tr>
              ) : team.map((member, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <p className="font-bold text-navy-900">{member.name}</p>
                    <p className="text-xs text-slate-500 font-medium">{member.role}</p>
                  </td>
                  <td className="p-4 border-l border-slate-100"><StatusIcon status={member.safeguarding} /></td>
                  <td className="p-4 border-l border-slate-100"><StatusIcon status={member.mca} /></td>
                  <td className="p-4 border-l border-slate-100"><StatusIcon status={member.mh} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManagerCompliance;